/**
 * Curated Okendo photo strip.
 *
 * Okendo's own Media Grid picks its tiles itself: the widget asks the API for
 * `reviews?limit=25&orderBy=has_media desc` and shows whatever comes back, so
 * there is no way to pin a particular photo through its data attributes. When
 * the section carries a list of review IDs we skip the widget and build the
 * strip ourselves from the same public API, one request per pinned review, and
 * keep the merchant's order.
 *
 * The API is public and answers with `access-control-allow-origin: *`, so this
 * runs straight from the storefront with no key.
 *
 * Dropping the widget also drops its lightbox, and Okendo exposes no way to
 * open that for a given review, so the strip carries its own: the API hands us
 * the whole review, which is everything the modal needs to show.
 */

const API = 'https://api.okendo.io/v1/stores';

/** Okendo serves any size off the media key, so ask for what the tile needs. */
function mediaUrl(media, px) {
  return `https://media-dynamic.okendo.io/images/${media.dynamicKey}.jpg?d=${px}x${px}&crop=center`;
}

/** Uncropped, for the lightbox, where the whole photo should be visible. */
function fullUrl(media) {
  return `https://media-dynamic.okendo.io/images/${media.dynamicKey}.jpg?d=1600x1600`;
}

function photosOf(review) {
  const media = review.media || [];
  return media.filter((m) => m.type !== 'video').length ? media.filter((m) => m.type !== 'video') : media;
}

function altFor(review, media, pick) {
  // Okendo writes alt text for newer photos only; older ones come back null,
  // and a review title ("Stunning Apollo!") does not describe the picture.
  // Its generated descriptions are also occasionally just wrong, which is what
  // a per-line override is for.
  return pick?.alt || media.alt || (review.productName ? `Customer photo of ${review.productName}` : review.title || '');
}

/**
 * One pinned line: `reviewId | alt text | link`. The last two are optional,
 * an empty field keeps whatever Okendo returned, and `none` as the link drops
 * the product link - the right answer when a review sits on an add-on such as
 * shipping insurance rather than on a poster.
 */
function parsePick(line) {
  const [id, alt, link] = line.split('|').map((part) => part.trim());
  return id ? { id, alt: alt || null, link: link || null } : null;
}

function stars(rating) {
  return '★'.repeat(Math.round(rating || 0));
}

async function fetchReview(store, id) {
  try {
    const res = await fetch(`${API}/${store}/reviews/${id}`);
    if (!res.ok) return null;
    const { review } = await res.json();
    return review || null;
  } catch {
    return null;
  }
}

/* ---------- lightbox ---------- */

let dialog;

function buildDialog() {
  if (dialog) return dialog;

  dialog = document.createElement('dialog');
  dialog.className = 'ag-lightbox';
  dialog.innerHTML = `
    <button class="ag-lightbox__close" type="button" aria-label="Close">&times;</button>
    <div class="ag-lightbox__media">
      <img class="ag-lightbox__photo" alt="">
      <div class="ag-lightbox__thumbs" hidden></div>
    </div>
    <div class="ag-lightbox__body">
      <div class="ag-lightbox__stars"></div>
      <h3 class="ag-lightbox__title"></h3>
      <p class="ag-lightbox__text"></p>
      <div class="ag-lightbox__meta"></div>
      <a class="ag-lightbox__product" href="#"></a>
    </div>`;

  dialog.querySelector('.ag-lightbox__close').addEventListener('click', () => dialog.close());
  // Clicks that land on the dialog itself are backdrop clicks; the panes stop there.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  document.body.append(dialog);
  return dialog;
}

function openReview(review, verifiedLabel, pick) {
  const el = buildDialog();
  const photos = photosOf(review);
  const photo = el.querySelector('.ag-lightbox__photo');

  const show = (media) => {
    photo.src = fullUrl(media);
    photo.alt = altFor(review, media, pick);
  };
  show(photos[0]);

  const thumbs = el.querySelector('.ag-lightbox__thumbs');
  thumbs.replaceChildren();
  thumbs.hidden = photos.length < 2;
  photos.forEach((media, i) => {
    if (photos.length < 2) return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ag-lightbox__thumb';
    b.setAttribute('aria-label', `Photo ${i + 1} of ${photos.length}`);
    b.style.backgroundImage = `url("${mediaUrl(media, 200)}")`;
    b.addEventListener('click', () => {
      show(media);
      thumbs.querySelectorAll('.ag-lightbox__thumb').forEach((t) => t.removeAttribute('aria-current'));
      b.setAttribute('aria-current', 'true');
    });
    if (i === 0) b.setAttribute('aria-current', 'true');
    thumbs.append(b);
  });

  el.querySelector('.ag-lightbox__stars').textContent = stars(review.rating);
  el.querySelector('.ag-lightbox__title').textContent = review.title || '';
  el.querySelector('.ag-lightbox__text').textContent = review.body || '';

  const reviewer = review.reviewer || {};
  const country = reviewer.location?.country?.name;
  const meta = [
    reviewer.displayName,
    country,
    reviewer.isVerified ? verifiedLabel : null,
  ].filter(Boolean);
  el.querySelector('.ag-lightbox__meta').textContent = meta.join(' · ');

  // Reviews imported before the current catalogue come back without a product,
  // and `none` drops a link that points somewhere unhelpful.
  const link = el.querySelector('.ag-lightbox__product');
  const href = pick?.link === 'none' ? null : pick?.link || review.productUrl;
  if (href && review.productName) {
    link.href = href;
    link.textContent = review.productName;
    link.hidden = false;
  } else {
    link.hidden = true;
  }

  el.showModal();
}

/* ---------- strip ---------- */

function tile(review, strip, pick) {
  const photos = photosOf(review);
  const media = photos[0];
  if (!media) return null;

  const fallback = strip.dataset.agOkendoFallback || '#';
  const link = document.createElement('a');
  link.className = 'ag-walls__photo';
  // productUrl comes back protocol-relative ("//astrography.com/products/…").
  // Kept as a real href so the tile still opens something in a new tab.
  link.href = pick?.link === 'none' ? fallback : pick?.link || review.productUrl || fallback;

  const img = document.createElement('img');
  img.src = mediaUrl(media, 600);
  img.srcset = `${mediaUrl(media, 600)} 600w, ${mediaUrl(media, 900)} 900w`;
  img.sizes = '(max-width: 749px) 45vw, 16vw';
  img.alt = altFor(review, media, pick);
  img.loading = 'lazy';
  img.decoding = 'async';
  link.append(img);

  if (review.rating) {
    const pill = document.createElement('span');
    pill.className = 'ag-walls__photo-stars';
    pill.textContent = stars(review.rating);
    link.append(pill);
  }

  link.addEventListener('click', (event) => {
    // Leave modified clicks alone so "open in new tab" still reaches the product.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    openReview(review, strip.dataset.agOkendoVerified || 'Verified buyer', pick);
  });

  return link;
}

for (const strip of document.querySelectorAll('[data-ag-okendo-picks]')) {
  if (strip.dataset.agOkendoDone) continue;
  strip.dataset.agOkendoDone = '1';

  const store = strip.dataset.agOkendoStore;
  const picks = strip.dataset.agOkendoPicks.split(/\r?\n/).map(parsePick).filter(Boolean);
  if (!store || !picks.length) continue;

  // Fetch in parallel, render in the order the merchant listed.
  const reviews = await Promise.all(picks.map((pick) => fetchReview(store, pick.id)));
  const tiles = reviews.map((r, i) => (r ? tile(r, strip, picks[i]) : null)).filter(Boolean);

  // A strip that resolved to nothing keeps its placeholders rather than
  // collapsing the section to a gap.
  if (!tiles.length) continue;
  strip.replaceChildren(...tiles);
}
